import { LinearProgress } from "@material-ui/core"
import PropTypes from "prop-types"
import { useContext, useEffect, useRef, useState } from "react"
import { useQuery } from "react-query"
import { listCourses } from "~/actions/courses"
import AdminActionBar from "~/components/Common/AdminActionBar"
import StatusFilters from "~/components/Common/StatusFilters"
import CourseCardList from "~/components/courses/CourseCardList"
import AppContext from "~/contexts/App"
import Courses from "~/contexts/Courses"
import Page from "~/pages/Page"
import Router from "~/router"

import { listCategories } from "~/actions/categories"
import { listVaasLocations } from "~/actions/courses"
import Box from "~/components/Common/Box"
import CourseClientFilter from "~/components/courses/CourseClientFilter"
import CourseTypeFilter from "~/components/courses/CourseTypeFilter"
import VaasLocationFilter from "~/components/courses/VaasLocationFilter"
import useStyles from "./styles"

const List = ({ routes = [], ...rest }) => {
  const classes = useStyles()
  const { openDialog, setPageSubTitle, setPageTitle } = useContext(AppContext)
  const [coursesType, setCoursesType] = useState("all")
  const [client, setClient] = useState(0)
  const [filteredData, setFilteredData] = useState([])
  const [vaasLocation, setVaasLocation] = useState("all")
  const { createCourseMutation } = useContext(Courses)
  const { data: categories } = useQuery("categories", () => listCategories())

  const searchInputRef = useRef(null)
  //set search value
  const [searchValue, setSearchValue] = useState("")
  //set sort data
  const [sort, setSort] = useState({
    name: "",
    type: "",
    direction: "",
    icon: "",
  })

  const filterOptions = [
    { id: 1, name: "Name" },
    { id: 2, name: "Lessons" },
    { id: 3, name: "Last Updated" },
    { id: 4, name: "Labs" },
  ]

  const { data: courses, isLoading: coursesLoading } = useQuery("courses", () =>
    listCourses(),
  )

  const { data: vaasLocations } = useQuery("vaasLocations", () =>
    listVaasLocations(),
  )

  //set courses title and subtitle
  useEffect(() => {
    setPageTitle("Courses")
  }, [courses])

  // filter courses by type (all, shortcode, course, labs) or category
  useEffect(() => {
    if (coursesType === "all" && client === 0) {
      setFilteredData(courses)
    } else {
      let filtered = [...courses]

      // Filter by course type
      switch (coursesType) {
        case "vaas":
          filtered = filtered.filter((course) => course.course_type === "vaas")
          break
        case "shortcode":
          filtered = filtered.filter(
            (course) => course.course_type === "shortcode",
          )
          break
        case "labs":
          filtered = filtered.filter((course) => course.lab_id !== null)
          break
        case "course":
          filtered = filtered.filter(
            (course) => course.course_type === "course",
          )
          break
        default:
          break
      }

      // Filter by client if needed
      if (client !== 0) {
        filtered = filtered.filter((course) =>
          course.categories.some((category) => category.id === client),
        )
      }
      if (vaasLocation !== "all" && coursesType === "vaas") {
        filtered = filtered.filter((course) =>
          course.vaas_domains.includes(vaasLocation),
        )
      }

      setFilteredData(filtered)
    }

    //clear search value when changing course type
    searchInputRef.current.value = ""

    setSearchValue("")
  }, [coursesType, client, vaasLocation])

  //reusable dialog to create lessons, courses, clients and instructor
  //pass a title, subtitle, type (for example course) a create mutation func and refetch data
  const openCreateDialog = () => {
    let data = {
      title: "New Course",
      subtitle: "Create a new Course",
      type: "course",
      create: createCourseMutation,
    }
    openDialog("createCourseLessonDialog", data)
  }
  const hash = window.location.hash
    ? window.location.hash.replace("#", "")
    : "published"
  //Course Status
  const [statusState, setStatusState] = useState(hash)
  const watchHash = () => {
    const newHash = window.location.hash.replace("#", "")
    if (newHash) {
      setStatusState(newHash)
    }
  }
  useEffect(() => {
    window.removeEventListener("hashchange", watchHash)
    window.addEventListener("hashchange", watchHash, false)
  })
  //set the length of each type of course
  const [statusNumber, setStatusNumber] = useState({
    published: null,
    unpublished: null,
    draft: null,
    archived: null,
    scheduled: null,
  })

  if (sort?.direction === "") {
    sort.name = ""
    sort.type = ""
  }

  const handleChangeCoursesType = (event) => {
    setCoursesType(event.target.value)
    setVaasLocation("all")
  }
  const handleChangeClient = (event) => {
    setClient(event.target.value)
    setVaasLocation("all")
  }

  const handleChangeVaasLocation = (event) => {
    setVaasLocation(event.target.value)
  }

  return (
    <Page>
      <AdminActionBar
        buttonName="New Course"
        buttonFunc={openCreateDialog}
        sortOptions={filterOptions}
        sortState={sort}
        setSortState={setSort}
        setFilterState={setSearchValue}
        placeholder="Search courses"
        searchInputRef={searchInputRef}
      />
      {coursesLoading ? (
        <LinearProgress color="primary" />
      ) : (
        <Box
          display="flex"
          height="100%"
          className={classes.container}
          flexWrap="wrap"
        >
          <Box className={classes.leftContainer}>
            <CourseTypeFilter
              courseType={coursesType}
              handleChangeCourseType={handleChangeCoursesType}
            />
            {coursesType === "vaas" && (
              <VaasLocationFilter
                options={vaasLocations}
                vaasLocation={vaasLocation}
                handleChangeVaasLocation={handleChangeVaasLocation}
              />
            )}
            <CourseClientFilter
              client={client}
              handleChangeClient={handleChangeClient}
              options={categories}
            />
            <StatusFilters
              setStatusState={setStatusState}
              statusState={statusState}
              statusNumber={statusNumber}
              type="courses"
            />
          </Box>
          <Box className={classes.rightContainer}>
            <CourseCardList
              searchValue={searchValue}
              sort={sort}
              openCreateDialog={openCreateDialog}
              statusState={statusState}
              setStatusNumber={setStatusNumber}
              filteredCoursesByType={filteredData}
              coursesLength={courses?.length}
            />
          </Box>
        </Box>
      )}

      <Router routes={routes} {...rest} />
    </Page>
  )
}

List.propTypes = {
  routes: PropTypes.array,
}

export default List
